import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { fetchFilteredInventory } from "../../http/api";
import {
  normalizeCusip,
  useSelectedLadderOrdersStore,
} from "../../Store/selectedLadderOrdersStore";

export const useInventoryData = (token, input, page, itemsPerPage, source = "noSource", selectedRung, refreshTrigger, sortConfigForFetchingData = null) => {
  const [bondData, setBondData] = useState([]);
  const [apiTotalElements, setApiTotalElements] = useState(0); // Original count from API
  const [totalElements, setTotalElements] = useState(0); // Filtered count after client-side filtering
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedOrders } = useSelectedLadderOrdersStore();

  useEffect(() => {
    const fetchInventory = async () => {
      // Clear existing data when starting a new fetch
      setBondData([]);
      setLoading(true);
      try {
        // Remove source from input to prevent it from being sent to the backend
        const { source: _source, ...cleanInput } = input || {}; // Destructure source out
       
        const payload = { 
          ...cleanInput, 
          page, 
          size: itemsPerPage,
          ...(sortConfigForFetchingData && {
            columnToSort: sortConfigForFetchingData.columnToSort,
            sortingDirection: sortConfigForFetchingData.sortingDirection,
          }),
        };
        
        if ((source === "filteredSource" || source === "viewInventoryBondChar" || source === "viewInventoryLadder") && !input) {
          setLoading(false);
          return; // Early return if no input for filteredSource
        }
        
        const data = await fetchFilteredInventory(token, payload);
        
        // Store the total from API
        const apiTotal = data.totalElements || data.totalItems || 0;
        setApiTotalElements(apiTotal);
        
        // Hide CUSIPs selected on other rungs, regardless of bondInventoryId.
        const selectedCusips = new Set(Object.entries(selectedOrders)
          .filter(([rungIndex, order]) => rungIndex !== String(selectedRung))
          .map(([, order]) => normalizeCusip(order?.cusip))
          .filter(Boolean));
        
        // Filter out bonds whose CUSIP is already selected on another rung.
        const filteredData = data.content.filter(
          bond => !selectedCusips.has(normalizeCusip(bond?.cusip))
        );
        
        // Check if selectedRung hasn't changed during the fetch
        // This ensures we don't set stale data if the user switched rungs quickly
        setBondData(filteredData);
        
        // Update the total elements count based on filtering
        // If we've filtered items out from the current page, adjust the total accordingly
        const itemsFilteredOut = data.content.length - filteredData.length;
        const adjustedTotal = Math.max(0, (data.totalElements || data.totalItems || 0) - itemsFilteredOut);
        setTotalElements(adjustedTotal);
        
        setLastUpdatedAt(dayjs().format("YYYY-MM-DD HH:mm:ss"));
      } catch (error) {
        console.error("Error fetching data:", error);
        setBondData([]);
        setTotalElements(0);
        setApiTotalElements(0);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      if (source === "noSource") {
        fetchInventory();
      } else if ((source === "filteredSource" || source === "viewInventoryBondChar" || source === "viewInventoryLadder") && input) {
        fetchInventory();
      }
    }, 100); // Small delay to prevent rapid consecutive fetches

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [token, input, page, itemsPerPage, selectedRung, selectedOrders, refreshTrigger, sortConfigForFetchingData]);

  return { 
    bondData, 
    totalElements, 
    apiTotalElements, // Include original API count if needed elsewhere
    lastUpdatedAt, 
    loading, 
    source 
  };
};
